import { useCallback, useEffect, useMemo, useState } from "react";
import BinScanner from "@/components/feature/BinScanner";
import OpsHeader from "@/components/feature/OpsHeader";
import ProductScanner from "@/components/feature/ProductScanner";
import { useAuth } from "@/hooks/useAuth";
import { useOpsWarehouse } from "@/hooks/useOpsWarehouse";
import {
  errorText,
  opsApi,
  qtyText,
  type PutawayLine,
  type ScannedBin,
} from "@/lib/warehouseOps";

/**
 * Đưa hàng vào vị trí: quét ô → quét mã hàng → xác nhận.
 *
 * Quét ô trước vì người cầm máy đang đứng trước một kệ cụ thể. Quét mã hàng ở
 * bước hai vì trong kho lúc nào cũng có nhiều dòng chờ cất, và bấm chọn trong
 * danh sách là mời người ta bấm nhầm dòng bên cạnh.
 *
 * Danh sách bấm tay vẫn còn, nhưng gập lại và nói rõ đó là **hàng chờ cất của
 * cả kho**, không phải hàng đang nằm trong ô — hai thứ này mà lẫn nhau thì
 * người dùng tưởng ô trống đang có hàng.
 */
export default function OpsPutawayPage() {
  const warehouse = useOpsWarehouse();
  const { getToken } = useAuth();
  const token = getToken() ?? undefined;

  const [bin, setBin] = useState<ScannedBin | null>(null);
  const [lines, setLines] = useState<PutawayLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  /** Mã hàng vừa quét: thu hẹp danh sách xuống đúng mặt hàng đó. */
  const [scannedCode, setScannedCode] = useState("");
  /** Mở danh sách bấm tay — lối dự phòng khi tem trên thùng hỏng. */
  const [browsing, setBrowsing] = useState(false);
  const [search, setSearch] = useState("");

  const [confirming, setConfirming] = useState<PutawayLine | null>(null);
  /** Dòng đang xác nhận đến từ quét mã hay từ bấm tay. */
  const [byScan, setByScan] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!warehouse) return;

    setLoading(true);
    setError("");

    try {
      setLines(await opsApi.putawayLines(warehouse.id, token));
    } catch (e) {
      setError(errorText(e, "Không tải được danh sách hàng chờ cất."));
      setLines([]);
    } finally {
      setLoading(false);
    }
  }, [warehouse, token]);

  useEffect(() => { if (bin) void load(); }, [bin, load]);

  /** Các mã hàng đang chờ cất — dùng để chặn ngay khi quét nhầm thùng. */
  const waitingCodes = useMemo(
    () => [...new Set(lines.map((l) => l.productCode))],
    [lines],
  );

  /** Dòng của đúng mặt hàng vừa quét; nhiều dòng nghĩa là nhiều lô. */
  const scannedLines = useMemo(
    () => lines.filter((l) => l.productCode === scannedCode),
    [lines, scannedCode],
  );

  /** Danh sách bấm tay: dòng hẹn đúng ô vừa quét lên trước. */
  const browseLines = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = q
      ? lines.filter((l) =>
          [l.productCode, l.productName, l.batchCode, l.taskCode, l.supplierName]
            .some((v) => (v ?? "").toLowerCase().includes(q)))
      : lines;

    const here = (l: PutawayLine) => (bin && l.reservedBinCode === bin.code ? 0 : 1);

    return [...matched].sort((a, b) => here(a) - here(b) || a.productCode.localeCompare(b.productCode));
  }, [lines, search, bin]);

  /* Quét ra mặt hàng chỉ có đúng một dòng chờ cất thì vào thẳng màn xác nhận. */
  useEffect(() => {
    if (scannedLines.length === 1) {
      setConfirming(scannedLines[0]);
      setByScan(true);
    }
  }, [scannedLines]);

  const confirm = async () => {
    if (!warehouse || !bin || !confirming) return;

    setSaving(true);
    setError("");

    try {
      await opsApi.confirmPutaway(
        confirming.taskId,
        confirming.itemId,
        bin.id,
        bin.code,
        byScan ? confirming.productCode : "",
        token,
      );

      setFlash(`Đã cất ${confirming.productCode} vào ô ${bin.code}.`);
      setConfirming(null);
      setScannedCode("");

      // Đọc lại đúng ô vừa cất: phần "đang chứa" phải kể cả kiện vừa đặt vào,
      // nếu không người cầm máy tưởng thao tác chưa ăn.
      const [, fresh] = await Promise.all([
        load(),
        opsApi.scanBin(warehouse.id, bin.code, token).catch(() => null),
      ]);

      if (fresh) setBin(fresh.data);
    } catch (e) {
      setError(errorText(e, "Không xác nhận được. Thử lại."));
    } finally {
      setSaving(false);
    }
  };

  if (!warehouse) return null;

  // ─── Bước 1: quét ô ────────────────────────────────────────────────────────
  if (!bin) {
    return (
      <div className="min-h-screen bg-slate-50">
        <OpsHeader title="Đưa hàng vào vị trí" subtitle="Bước 1 / 3 · quét ô" warehouseName={warehouse.name} />
        <div className="p-4 space-y-3">
          <BinScanner
            warehouseId={warehouse.id}
            token={token}
            label="Quét mã ô sẽ cất hàng vào"
            hint="Quét ô trước, rồi mới quét mã hàng cần cất vào đó. Tem mờ thì gõ vài ký tự và chọn ô trong danh sách."
            searchable
            onScanned={(b) => { setBin(b); setScannedCode(""); setBrowsing(false); }}
          />
          {flash && (
            <p className="text-sm text-brand-700 bg-brand-50 border border-brand-200 rounded-lg px-3 py-2">
              {flash}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ─── Bước 2: quét mã hàng ─────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 pb-6">
      <OpsHeader
        title={`Cất vào ô ${bin.code}`}
        subtitle="Bước 2 / 3 · quét mã hàng"
        warehouseName={warehouse.name}
        onBack={() => { setBin(null); setScannedCode(""); setSearch(""); setBrowsing(false); setFlash(""); }}
      />

      <div className="p-4 space-y-3">
        {/* Nội dung THẬT của ô — tách hẳn khỏi danh sách hàng chờ cất bên dưới. */}
        <div className="bg-white rounded-xl border border-slate-200 p-3">
          <p className="text-xs text-slate-500">Ô đang mở</p>
          <p className="font-mono text-sm text-slate-900 break-all">{bin.code}</p>
          {bin.zoneName && <p className="text-xs text-slate-500 mt-0.5">Khu {bin.zoneName}</p>}

          <p className="text-xs font-medium text-slate-600 mt-2">Hàng đang nằm trong ô</p>
          {bin.contents.length === 0 ? (
            <p className="text-xs text-slate-400 mt-0.5">Ô đang trống.</p>
          ) : (
            <ul className="mt-0.5 space-y-0.5">
              {bin.contents.map((c) => (
                <li key={c.id} className="text-xs text-slate-600">
                  <span className="font-mono">{c.productCode}</span>
                  {c.lotNumber ? ` · Lô ${c.lotNumber}` : ""} — {qtyText(c.qty)} {c.unit}
                </li>
              ))}
            </ul>
          )}

          <button
            onClick={() => { setBin(null); setScannedCode(""); setFlash(""); }}
            className="mt-2 text-sm font-medium text-brand-700"
          >
            Quét ô khác
          </button>
        </div>

        {flash && (
          <p className="text-sm text-brand-700 bg-brand-50 border border-brand-200 rounded-lg px-3 py-2">{flash}</p>
        )}
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
        )}

        {loading ? (
          <p className="text-center text-slate-400 py-6">Đang tải danh sách hàng chờ cất...</p>
        ) : lines.length === 0 ? (
          <p className="text-center text-slate-400 py-6">Kho này không còn dòng nào chờ cất.</p>
        ) : (
          <>
            <ProductScanner
              token={token}
              label="Quét mã hàng trên thùng đang cầm"
              hint={`Kho đang có ${lines.length} dòng chờ cất.`}
              expected={waitingCodes}
              expectedLabel="Mặt hàng này không nằm trong danh sách chờ cất của kho."
              onScanned={(p) => { setScannedCode(p.code); setError(""); }}
            />

            {/* Một mã hàng có thể có nhiều lô đang chờ cất: phải chọn đúng lô. */}
            {scannedCode && scannedLines.length > 1 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-600">
                  {scannedCode} đang có {scannedLines.length} lô chờ cất — chọn lô đang cầm
                </p>
                {scannedLines.map((l) => (
                  <button
                    key={`${l.taskId}-${l.itemId}`}
                    onClick={() => { setConfirming(l); setByScan(true); }}
                    className="w-full text-left bg-white rounded-xl border border-brand-300 p-3 active:bg-slate-50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-slate-900 text-sm truncate">
                        {l.batchCode ? `Lô ${l.batchCode}` : "Không có số lô"}
                      </p>
                      <span className="shrink-0 text-xs text-slate-600">
                        {qtyText(l.qtyToPutaway)} {l.unit}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {l.expDate ? `HSD ${l.expDate} · ` : ""}{l.taskCode}
                    </p>
                  </button>
                ))}
              </div>
            )}

            {scannedCode && scannedLines.length === 0 && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {scannedCode} không còn dòng nào chờ cất.
              </p>
            )}

            {/* Lối dự phòng: tem trên thùng mờ thì bấm chọn tay. Gập lại để
                không bị đọc nhầm thành nội dung của ô. */}
            <div className="bg-white rounded-xl border border-slate-200">
              <button
                onClick={() => setBrowsing((v) => !v)}
                className="w-full flex items-center justify-between gap-2 px-3 py-3 text-left"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-700">
                    Hàng chờ cất của cả kho ({lines.length} dòng)
                  </span>
                  <span className="block text-xs text-slate-400 mt-0.5">
                    Không phải hàng trong ô — chỉ dùng khi tem trên thùng không quét được
                  </span>
                </span>
                <span className="text-slate-400 shrink-0">{browsing ? "▲" : "▼"}</span>
              </button>

              {browsing && (
                <div className="px-3 pb-3 space-y-2">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Tìm mã hàng, tên hàng, số lô..."
                    className="w-full px-3 py-2.5 text-sm border-2 border-slate-200 rounded-lg focus:outline-none focus:border-brand-500"
                  />
                  {browseLines.length === 0 ? (
                    <p className="text-center text-slate-400 py-6 text-sm">Không có dòng nào khớp.</p>
                  ) : (
                    browseLines.map((l) => {
                      const here = l.reservedBinCode === bin.code;

                      return (
                        <button
                          key={`${l.taskId}-${l.itemId}`}
                          onClick={() => { setConfirming(l); setByScan(false); }}
                          className={`w-full text-left rounded-lg border p-3 active:bg-slate-50 ${
                            here ? "border-brand-400" : "border-slate-200"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-slate-900 text-sm truncate">{l.productName}</p>
                            <span className="shrink-0 text-xs text-slate-600">
                              {qtyText(l.qtyToPutaway)} {l.unit}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 font-mono mt-0.5">
                            {l.productCode}
                            {l.batchCode ? ` · Lô ${l.batchCode}` : ""}
                            {l.expDate ? ` · HSD ${l.expDate}` : ""}
                          </p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            {here ? (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-600 text-white">
                                ĐÚNG Ô ĐÃ HẸN
                              </span>
                            ) : l.reservedBinCode ? (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-mono">
                                Hẹn ô {l.reservedBinCode}
                              </span>
                            ) : l.reservedZoneName ? (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">
                                Hẹn khu {l.reservedZoneName}
                              </span>
                            ) : null}
                            <span className="text-[10px] text-slate-400 truncate">{l.taskCode}</span>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Bước 3: nhắc lại đúng ô và đúng hàng trước khi ghi. */}
      {confirming && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-end">
          <div className="w-full bg-white rounded-t-2xl p-4 space-y-3">
            <div>
              <p className="text-xs text-slate-500">Bước 3 / 3 · xác nhận</p>
              <p className="font-semibold text-slate-900 mt-0.5">{confirming.productName}</p>
              <p className="text-xs text-slate-500 font-mono mt-0.5">
                {confirming.productCode}
                {confirming.batchCode ? ` · Lô ${confirming.batchCode}` : ""}
              </p>
            </div>

            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 space-y-1">
              <p className="text-sm text-slate-700">
                Cất <strong>{qtyText(confirming.qtyToPutaway)} {confirming.unit}</strong> vào ô
              </p>
              <p className="font-mono text-sm text-slate-900 break-all">{bin.code}</p>
              {!byScan && (
                <p className="text-xs text-amber-700">
                  Dòng này chọn bằng tay, không qua quét mã hàng.
                </p>
              )}
              {confirming.reservedBinCode && confirming.reservedBinCode !== bin.code && (
                <p className="text-xs text-amber-700">
                  Đơn mua hẹn ô {confirming.reservedBinCode} — bạn đang cất sang ô khác.
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => { setConfirming(null); setScannedCode(""); }}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-700 font-medium"
              >
                Hủy
              </button>
              <button
                onClick={() => void confirm()}
                disabled={saving}
                className="flex-1 py-3 rounded-xl bg-brand-600 text-white font-semibold disabled:opacity-60"
              >
                {saving ? "Đang lưu..." : "Xác nhận đã cất"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
